import axios, { type AxiosProgressEvent } from 'axios';

import { type Common } from '../common.js';
import {
  type CreateValues,
  type PracticeTheme,
  type Theme,
  type UpdateValues,
} from '../models/practice/theme.js';
import { isTruthy } from '../utils.js';

interface Entity {
  id: string;
  name: string;
}

interface NotInheritingEntities {
  practices: Entity[];
  practiceGroups?: Entity[];
}

interface InheritingEntity {
  inheritanceOwner: string;
  inheritanceOwnerId: string;
}

export class API {
  common: Common;
  baseEndpoint = '/practice-themes';

  constructor(common: Common) {
    this.common = common;
  }

  async findOneByPracticeId(practiceId: string): Promise<PracticeTheme> {
    const url = this.common.constructPath(this.baseEndpoint, {
      practiceId,
    });
    return await this.common.getJSON(url);
  }

  async findOneByPracticeGroupId(practiceGroupId: string): Promise<PracticeTheme> {
    const url = this.common.constructPath(this.baseEndpoint, {
      practiceGroupId,
    });
    return await this.common.getJSON(url);
  }

  async findOneByOrganizationId(organizationId: string): Promise<PracticeTheme> {
    const url = this.common.constructPath(this.baseEndpoint, {
      organizationId,
    });
    return await this.common.getJSON(url);
  }

  async findThemeByPracticeId(practiceId: string): Promise<Theme> {
    const url = this.common.constructPath(`/practice/${practiceId}/theme`);
    return await this.common.getJSON(url);
  }

  async findThemeByOrganizationId(organizationId: string): Promise<Theme> {
    const url = this.common.constructPath(`/organizations/${organizationId}/theme`);
    return await this.common.getJSON(url);
  }

  async findThemeByPracticeGroupId(practiceGroupId: string): Promise<Theme> {
    const url = this.common.constructPath(`/practice-groups/${practiceGroupId}/theme`);
    return await this.common.getJSON(url);
  }

  async findInheritanceOwnerByPracticeIdAndAttribute(
    practiceId: string,
    attribute: string
  ): Promise<InheritingEntity> {
    const url = this.common.constructPath(`/practice/${practiceId}/theme/inheritance-owner`, {
      attribute,
    });
    return await this.common.getJSON(url);
  }

  async findInheritanceOwnerByPracticeGroupIdAndAttribute(
    practiceGroupId: string,
    attribute: string
  ): Promise<InheritingEntity> {
    const url = this.common.constructPath(
      `/practice-groups/${practiceGroupId}/theme/inheritance-owner`,
      {
        attribute,
      }
    );
    return await this.common.getJSON(url);
  }

  async findNotIheritingEntitiesByOrganizationId(
    organizationId: string,
    attribute: string
  ): Promise<NotInheritingEntities> {
    const url = this.common.constructPath(
      `/organizations/${organizationId}/theme/not-inheriting-entities`,
      {
        attribute,
      }
    );
    return await this.common.getJSON(url);
  }

  async findNotIheritingEntitiesByPracticeGroupId(
    practiceGroupId: string,
    attribute: string
  ): Promise<NotInheritingEntities> {
    const url = this.common.constructPath(
      `/practice-groups/${practiceGroupId}/theme/not-inheriting-entities`,
      {
        attribute,
      }
    );
    return await this.common.getJSON(url);
  }

  async create(values: CreateValues): Promise<PracticeTheme> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.postJSON(url, values);
  }

  async update(id: string, values: UpdateValues): Promise<PracticeTheme> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.patchJSON(url, values);
  }

  async getSignedUploadURL(
    practiceId: string | undefined,
    practiceGroupId: string | undefined,
    organizationId: string | undefined,
    attribute: keyof Theme
  ): Promise<string> {
    const url = this.common.constructPath(`${this.baseEndpoint}/upload-url`);
    const response = await this.common.postJSON(url, {
      practiceId,
      practiceGroupId,
      organizationId,
      attribute,
    });
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return response?.url || '';
  }

  async uploadImage(args: {
    file: File;
    practiceId: string | undefined;
    practiceGroupId: string | undefined;
    organizationId: string | undefined;
    attribute: keyof Theme;
  }): Promise<{
    gsPath: string;
    fileName: string;
    bucket: string;
  }> {
    const { file, practiceId, practiceGroupId, organizationId, attribute } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/upload-image`);
    const formData = new FormData();
    formData.append('image', file);
    formData.append('attribute', attribute);
    if (isTruthy(practiceId)) {
      formData.append('practiceId', practiceId);
    }
    if (isTruthy(practiceGroupId)) {
      formData.append('practiceGroupId', practiceGroupId);
    }
    if (isTruthy(organizationId)) {
      formData.append('organizationId', organizationId);
    }
    const response = await this.common.postFormData(url, formData);
    return response;
  }

  async upload(
    file: File,
    signedUploadURL: string,
    onUploadProgress: (event: AxiosProgressEvent) => void
  ): Promise<number> {
    const response = await axios.put(signedUploadURL, file, {
      onUploadProgress,
      headers: { 'Content-Type': 'image/png' },
    });
    return response.status;
  }

  async findLogoByQuery(query: {
    practiceId?: string;
    practiceGroupId?: string;
    organizationId?: string;
  }): Promise<Pick<
    Theme,
    | 'previsitLogoUrl'
    | 'passbookLogoUrl'
    | 'mastheadImageUrl'
    | 'ssoButtonIconUrl'
    | 'previsitFaviconUrl'
  > | null> {
    const url = this.common.constructPath(`${this.baseEndpoint}/logo`, query);
    return await this.common.getJSON(url);
  }
}
